import { useState, useEffect, useCallback, useRef } from 'react'
import { FiSlash, FiLoader, FiPlus, FiRotateCcw, FiAlertTriangle, FiCheckCircle, FiSearch, FiTrash2 } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import ConfirmDialog from './ConfirmDialog'

/**
 * Seção "Líderes" do painel Bloqueados — líderes que não recebem lead (admin).
 *
 * Origem (17/07/2026): a Jaqueline (#11040058) pediu baixa, a empresária do
 * distrito avisou, e ela recebeu um contato na madrugada seguinte. Enquanto a
 * baixa não é efetivada no DRACO, a sync do Oracle (60s) devolve a líder pra
 * fila do auto-assign — e mexer no cadastro não segura, porque a sync
 * sobrescreve. Antes disso o único jeito era editar código e rebuildar.
 *
 * Bloquear aqui vale pros DOIS caminhos: o auto-assign nunca escolhe a líder, e
 * o handoff manual é barrado com 403 (gate em routes/leaders.cjs). Não mexe em
 * quem já está atribuído — realocar lead que já está na mão de alguém é decisão
 * humana, não efeito colateral de um clique.
 */

interface LeaderBlockEntry {
  id: number
  codigo: string
  leader_name: string | null
  reason: string
  blocked_by: string
  blocked_at: string | null
  active: number
  unblocked_by: string | null
  unblocked_at: string | null
  hit_count: number
  last_hit_at: string | null
  cadastro_name: string | null
  tipo_consultor: string | null
  cadastro_status: string | null
  grupo_nome: string | null
  endereco_cidade: string | null
}

interface LeaderHit {
  codigo: string
  nome: string
  grupo_nome: string | null
  endereco_cidade: string | null
  tipo_consultor: string
}

function fmtDate(s: string | null): string {
  if (!s) return '—'
  const iso = s.includes('T') ? s : s.replace(' ', 'T') + 'Z'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

// tipo_consultor 1=empresária, 2=líder. Qualquer outra coisa = o DRACO já não a
// trata mais como líder, ou seja, a baixa saiu e o bloqueio virou redundante.
function jaBaixou(e: LeaderBlockEntry): boolean {
  return !!e.tipo_consultor && e.tipo_consultor !== '1' && e.tipo_consultor !== '2'
}

export function LideresBloqueadasSection() {
  const { authenticatedFetch } = useAuth()
  const [entries, setEntries] = useState<LeaderBlockEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<LeaderHit[]>([])
  const [picked, setPicked] = useState<LeaderHit | null>(null)
  const [reason, setReason] = useState('')
  const [confirm, setConfirm] = useState<{ isOpen: boolean; entry: LeaderBlockEntry | null }>({ isOpen: false, entry: null })
  const [confirmDel, setConfirmDel] = useState<{ isOpen: boolean; entry: LeaderBlockEntry | null }>({ isOpen: false, entry: null })
  const searchSeq = useRef(0)

  const load = useCallback(async () => {
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LEADER_BLOCKLIST)
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      setEntries(d.entries)
    } catch (e) {
      setError((e as Error).message)
      setEntries([])
    }
  }, [authenticatedFetch])

  useEffect(() => { load() }, [load])

  // Busca com debounce. O seq guarda contra resposta fora de ordem — digitar
  // rápido faria a resposta de "jaq" chegar depois da de "jaque" e repor a lista
  // errada embaixo do dedo do usuário.
  useEffect(() => {
    if (picked || query.trim().length < 2) { setHits([]); return }
    const seq = ++searchSeq.current
    const t = setTimeout(async () => {
      try {
        const r = await authenticatedFetch(API_ENDPOINTS.LEADER_BLOCKLIST_SEARCH(query.trim()))
        const d = await r.json()
        if (seq === searchSeq.current && r.ok && d.success) setHits(d.leaders || [])
      } catch { /* busca é auxiliar — erro não trava o form */ }
    }, 250)
    return () => clearTimeout(t)
  }, [query, picked, authenticatedFetch])

  const resetForm = () => {
    setQuery(''); setHits([]); setPicked(null); setReason(''); setShowForm(false)
  }

  const submit = async () => {
    if (!picked || !reason.trim()) return
    setSaving(true)
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LEADER_BLOCKLIST, {
        method: 'POST',
        body: JSON.stringify({ codigo: picked.codigo, reason }),
      })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      resetForm()
      await load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const unblock = async (entry: LeaderBlockEntry) => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LEADER_BLOCKLIST_ITEM(entry.id), { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      await load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  // Remoção definitiva: só pra quem já foi desbloqueada. O backend recusa
  // (409) apagar bloqueio ativo — some da fila sem rastro seria o pior caso.
  const removeFromList = async (entry: LeaderBlockEntry) => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LEADER_BLOCKLIST_ITEM_PERMANENT(entry.id), { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      await load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const ativos = entries?.filter(e => e.active) ?? []
  const inativos = entries?.filter(e => !e.active) ?? []

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <span className="text-xs text-muted-foreground tabular-nums flex-1" title="Líderes com bloqueio ativo">
          {ativos.length} {ativos.length === 1 ? 'bloqueada' : 'bloqueadas'}
        </span>
        <button
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          title="Bloquear uma líder de receber novos leads"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-primary text-primary-foreground neu-relief hover:opacity-90 shrink-0"
        >
          <FiPlus className="w-4 h-4" />
          <span className="hidden sm:inline">Bloquear líder</span>
        </button>
      </div>

      <p className="text-[11px] text-muted-foreground mb-3 shrink-0 leading-relaxed">
        Uma líder bloqueada não recebe lead nem pelo auto-assign nem por atribuição manual — o botão de
        encaminhar dá erro pra quem tentar. Use quando ela pedir baixa: o DRACO leva um tempo até baixar
        de verdade, e até lá o sistema continuaria mandando contato pra ela. Não mexe nos leads que ela
        já tem em mãos.
      </p>

      {error && (
        <div className="mb-2 px-3 py-2 rounded-lg neu-tag bg-destructive/10 text-destructive text-xs shrink-0">{error}</div>
      )}

      {showForm && (
        <div className="mb-3 p-3 rounded-xl border border-border/60 bg-muted/30 shrink-0 space-y-2">
          {picked ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background border border-border">
              <FiCheckCircle className="w-4 h-4 text-emerald-500 shrink-0" title="Líder selecionada" />
              <span className="text-sm text-foreground truncate flex-1">
                {picked.nome}
                <span className="text-xs text-muted-foreground tabular-nums ml-2">#{picked.codigo}</span>
              </span>
              <button
                onClick={() => { setPicked(null); setQuery('') }}
                title="Escolher outra líder"
                className="text-[11px] text-muted-foreground hover:text-foreground shrink-0"
              >
                trocar
              </button>
            </div>
          ) : (
            <div className="relative">
              <FiSearch className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Buscar líder por nome ou código — ex: Jaqueline"
                title="Digite ao menos 2 letras do nome, ou o código do cadastro"
                className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground"
              />
              {hits.length > 0 && (
                <ul className="absolute z-10 left-0 right-0 mt-1 max-h-52 overflow-y-auto rounded-lg border border-border bg-background neu-pop divide-y divide-border/40">
                  {hits.map(h => (
                    <li key={h.codigo}>
                      <button
                        onClick={() => { setPicked(h); setHits([]) }}
                        title={`Bloquear ${h.nome}`}
                        className="w-full text-left px-3 py-2 hover:bg-muted/60"
                      >
                        <div className="text-sm text-foreground truncate">{h.nome}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          #{h.codigo}
                          {h.grupo_nome ? ` · ${h.grupo_nome}` : ''}
                          {h.endereco_cidade ? ` · ${h.endereco_cidade}` : ''}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {query.trim().length >= 2 && hits.length === 0 && (
                <p className="text-[10px] text-muted-foreground mt-1 px-1">
                  Nenhuma líder encontrada com esse nome (ou ela já está bloqueada).
                </p>
              )}
            </div>
          )}
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Motivo do bloqueio (obrigatório) — ex: pediu baixa, aguardando baixar no DRACO"
            title="O motivo fica registrado para auditoria"
            className="w-full px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { resetForm(); setError(null) }}
              className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-muted-foreground hover:text-foreground"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={saving || !picked || !reason.trim()}
              title={!picked ? 'Escolha a líder na busca acima' : !reason.trim() ? 'O motivo é obrigatório' : 'Confirmar bloqueio'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-red-600 hover:bg-red-700 text-white neu-relief disabled:opacity-60"
            >
              {saving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSlash className="w-4 h-4" />}
              {saving ? 'Bloqueando…' : 'Bloquear'}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-dashed border-border/60">
        {entries === null && (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        )}
        {entries && entries.length === 0 && (
          <div className="py-10 text-center text-muted-foreground text-sm px-6">
            Nenhuma líder bloqueada.
          </div>
        )}
        {entries && entries.length > 0 && (
          <ul className="space-y-1.5 py-1">
            {[...ativos, ...inativos].map(e => (
              <li
                key={e.id}
                className={`flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors ${e.active ? '' : 'opacity-50'}`}
              >
                <FiSlash
                  className={`w-4 h-4 shrink-0 ${e.active ? 'text-red-500' : 'text-muted-foreground'}`}
                  title={e.active ? 'Bloqueio ativo' : 'Desbloqueada'}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground truncate font-medium">
                      {e.leader_name || e.cadastro_name || 'Sem nome'}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0" title="Código no cadastro">
                      #{e.codigo}
                    </span>
                    {e.active === 1 && jaBaixou(e) && (
                      <span
                        className="shrink-0 inline-flex items-center gap-1 text-[9px] px-1 py-0.5 rounded neu-tag bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40 font-bold whitespace-nowrap cursor-help"
                        title="O DRACO já não a classifica mais como líder — a baixa saiu. O bloqueio virou redundante e pode ser removido."
                      >
                        <FiCheckCircle className="w-2.5 h-2.5" />
                        baixou no DRACO
                      </span>
                    )}
                    {e.hit_count > 0 && (
                      <span
                        className="shrink-0 inline-flex items-center gap-1 text-[9px] px-1 py-0.5 rounded neu-tag bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40 font-bold whitespace-nowrap cursor-help"
                        title={`O sistema tentou encaminhar lead pra ela ${e.hit_count}× depois do bloqueio (e foi barrado). Última vez: ${fmtDate(e.last_hit_at)}`}
                      >
                        <FiAlertTriangle className="w-2.5 h-2.5" />
                        {e.hit_count}× barrado
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate" title={e.reason}>
                    {e.reason}
                  </div>
                  <div className="text-[10px] text-muted-foreground/70 truncate">
                    {e.active
                      ? `Bloqueada por ${e.blocked_by} em ${fmtDate(e.blocked_at)}`
                      : `Desbloqueada por ${e.unblocked_by} em ${fmtDate(e.unblocked_at)}`}
                    {e.grupo_nome ? ` · ${e.grupo_nome}` : ''}
                    {e.endereco_cidade ? ` · ${e.endereco_cidade}` : ''}
                  </div>
                </div>
                {e.active === 1 ? (
                  <button
                    onClick={() => setConfirm({ isOpen: true, entry: e })}
                    title="Desbloquear esta líder"
                    className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-foreground hover:bg-muted/60 shrink-0"
                  >
                    <FiRotateCcw className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmDel({ isOpen: true, entry: e })}
                    title="Remover da lista definitivamente (o histórico deste bloqueio some)"
                    className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirm.isOpen}
        type="warning"
        title="Desbloquear líder?"
        message={
          confirm.entry
            ? `${confirm.entry.leader_name || confirm.entry.cadastro_name || `#${confirm.entry.codigo}`} volta a receber leads pelo auto-assign e por atribuição manual.`
            : ''
        }
        details={
          confirm.entry
            ? [
                `Motivo do bloqueio: ${confirm.entry.reason}`,
                ...(jaBaixou(confirm.entry)
                  ? ['O DRACO já não a classifica como líder — mesmo desbloqueada, ela não deve voltar à fila.']
                  : ['Atenção: o DRACO ainda a classifica como líder ativa — desbloquear devolve ela à fila de verdade.']),
              ]
            : undefined
        }
        confirmText="Desbloquear"
        cancelText="Manter bloqueada"
        onConfirm={() => {
          if (confirm.entry) unblock(confirm.entry)
          setConfirm({ isOpen: false, entry: null })
        }}
        onCancel={() => setConfirm({ isOpen: false, entry: null })}
      />

      <ConfirmDialog
        isOpen={confirmDel.isOpen}
        type="danger"
        title="Remover da lista?"
        message={
          confirmDel.entry
            ? `O registro de ${confirmDel.entry.leader_name || confirmDel.entry.cadastro_name || `#${confirmDel.entry.codigo}`} some do painel para sempre. Ela já está desbloqueada — isto não muda o que ela recebe, só apaga o histórico.`
            : ''
        }
        details={
          confirmDel.entry
            ? [
                `Motivo registrado: ${confirmDel.entry.reason}`,
                `Bloqueada por ${confirmDel.entry.blocked_by}, desbloqueada por ${confirmDel.entry.unblocked_by || '—'}.`,
              ]
            : undefined
        }
        confirmText="Remover"
        cancelText="Manter no histórico"
        onConfirm={() => {
          if (confirmDel.entry) removeFromList(confirmDel.entry)
          setConfirmDel({ isOpen: false, entry: null })
        }}
        onCancel={() => setConfirmDel({ isOpen: false, entry: null })}
      />
    </div>
  )
}
